import type { TranslatableEnum } from "../../types/models/TranslatableEnum.interface";

/**
 * A media usage `intent` as it arrives from the API.
 *
 * `MediaUsageIntent` is translated server-side (see the backend's
 * `BackedEnumTranslatable` normalizer), so it normalizes to an object carrying
 * both the raw `value` and the Dutch `label` rather than a bare string. The
 * union keeps the string form because not every endpoint serializes through
 * that normalizer, and because a cached response may still hold the old shape.
 */
export type MediaIntent = string | TranslatableEnum | null | undefined;

/**
 * The raw enum value (e.g. `damage_overview`), whichever shape the API used.
 *
 * Use this for logic — intent comparisons, lookup keys, classification. Never
 * branch on the label: it is display copy and may be retranslated.
 */
export const getMediaIntentValue = (intent: MediaIntent): string => {
  if (!intent) {
    return "";
  }

  return typeof intent === "string" ? intent : intent.value;
};

/**
 * True when the intent is the given raw enum value.
 *
 * Prefer this over `intent.includes(...)` on the raw field: once the intent is
 * an object that call silently returns `undefined`, which is falsy, so a check
 * for "has the driver supplied this document" flips to "no" and the UI asks
 * for something it already has.
 */
export const isMediaIntent = (intent: MediaIntent, value: string): boolean =>
  getMediaIntentValue(intent) === value;

/**
 * The human-readable Dutch label, preferring the server-provided translation.
 *
 * Falls back to the raw value when the API sent a bare string, so a consumer
 * reading an endpoint that bypasses the translating normalizer still renders
 * something meaningful rather than an empty cell.
 */
export const getMediaIntentLabel = (intent: MediaIntent): string => {
  if (!intent) {
    return "";
  }

  return typeof intent === "string" ? intent : (intent.label ?? intent.value);
};
